<script setup>
import { nextTick, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { getEssay, patchEssayLike } from '@/api/EowayApi';
import { IconLike } from '@/components/svg';
import {
  canonicalUrl,
  buildArticleTitle,
  buildArticleDescription,
  buildArticleHeadMeta,
  hasInlineSchema,
} from '@/utils/seo';
import {
  branchShareDataMap,
  convertPathToBranchPage,
  createBranchLink,
} from '@/composables/useBranch';
import { useSimpleLinkIntercept } from '@/composables/useInterceptInternalExternalLinks';
import { DEV_MODE } from '@/config/dev-mode';
import { BoardTitleBox } from '@/components/board';
import PostSharePopup from '@/components/nangman-center/PostSharePopup.vue';

definePage({ meta: { layout: 'essay' } });

const props = defineProps({
  essayId: {
    type: String,
    default: '',
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
// 웹 화면 여부
const isWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;
const contents = ref('');
const {
  VITE_KAKAO_CHANNEL_BASE_URL,
  VITE_KAKAO_CHANNEL_PUBLIC_ID,
  VITE_WEB_BASE_URL,
} = import.meta.env;

const ADD_KAKAO_CHANNEL_URL = `${VITE_KAKAO_CHANNEL_BASE_URL}/${VITE_KAKAO_CHANNEL_PUBLIC_ID}`;

const userId = store.getters['user/id'];

const essayData = ref(null);
const likeData = ref({ likeCnt: null, likeClicked: false });
const isBranchInitialized = ref(false);
let board = 'essay';

onMounted(async () => {
  console.log('========================================');
  console.log('route.params.essayId:', route.params.essayId);
  console.log('route.params.essayId:', route.params.essayId);
  console.log('========================================');
  /** 링크 공유를 통해 게시물로 오려면 페이지 마운트될 때 게시물을 호출해야 함 */
  await _getEssay();

  /** Branch SDK 로드 */
  const script = document.createElement('script');
  script.src = 'https://cdn.branch.io/branch-latest.min.js';
  script.async = true;
  document.head.appendChild(script);
  /** Branch - 스크립트 로드 완료 시 필요한 설정 */
  const options = { open_app: true };
  script.onload = () => {
    branch.init(import.meta.env.VITE_BRANCH_KEY, options, (err, data) => {
      if (err) {
        DEV_MODE && console.error('Branch SDK 초기화 중 오류:', err);
      } else {
        DEV_MODE && console.log('Branch SDK 초기화 성공:', data);
        isBranchInitialized.value = true;
      }
    });
  };
});

onUnmounted(() => {
  /** Branch - 언마운트 시 SDK 스크립트 제거 (선택 사항) */
  const script = document.querySelector('script[src="https://cdn.branch.io/branch-latest.min.js"]');
  if (script) {
    document.head.removeChild(script);
  }
});

// 에세이 상세 SEO 메타 (가이드 v1.4 시트 3 자동 생성 규칙)
useHead(() => {
  const data = essayData.value;
  if (!data?.mainTitle) return {};

  const fullTitle = buildArticleTitle(data.mainTitle, '낭만에세이');
  const description = buildArticleDescription({
    subtitle: data.subTitle,
    html: contents.value,
  });
  const image = data.thumbnail?.fileUri || data.attachFiles?.[0]?.publishedUri || undefined;
  const url = canonicalUrl(route.fullPath);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.mainTitle,
    description,
    url,
    datePublished: data.displayDate || data.regDt,
    dateModified: data.updDt || data.displayDate || data.regDt,
    author: { '@type': 'Organization', name: '낭만만세력' },
    publisher: { '@type': 'Organization', name: '낭만만세력' },
  };

  // 본문에 Schema.org 마크업이 인라인으로 들어있으면 자동 JSON-LD 생성 스킵
  const inlineSchemaPresent = hasInlineSchema(contents.value);

  return buildArticleHeadMeta({
    fullTitle,
    description,
    image,
    url,
    publishedAt: data.displayDate || data.regDt,
    modifiedAt: data.updDt,
    jsonLd: inlineSchemaPresent ? undefined : jsonLd,
  });
});

/** 에세이 상세 요청 */
const _getEssay = async () => {
  try {
    const result = await getEssay({ essayId: route.params.essayId });
    if (!result) router.go(-1);
    essayData.value = result;
    contents.value = isWebView
      ? essayData.value.contentsM?.trim() || essayData.value.contents || ''
      : essayData.value.contents || '';
    likeData.value.likeCnt = result.likes;
    likeData.value.likeClicked = result.likesChecked;
  } catch (error) {
    router.go(-1);
  }
};

/** 목록으로 이동 */
const goViewList = () => router.push(`/nangman-center/essay`);

/** 카카오 채널 추가하기 */
const addKakaoChannel = () => {
  window.open(ADD_KAKAO_CHANNEL_URL, '_blank');
};

/** 좋아요 버튼 클릭 */
const patchLike = async () => {
  try {
    const result = await patchEssayLike(route.params.essayId);
    likeData.value = result;
  } catch (error) {}
};

// ========================= 공유하기 =========================
const showPostSharePopup = ref(false);
const closePopSharePopup = () => (showPostSharePopup.value = false);

// ---- 모바일 환경 감지 ----
const ua = navigator.userAgent || '';
const isRN = typeof window.ReactNativeWebView !== 'undefined';
const isIOS = /iPad|iPhone|iPod/i.test(ua);
const isAndroid = /Android/i.test(ua);
const isAndroidWebView = isRN && isAndroid;

/** 공유하기 데이터 */
const shareItem = ref(null);

const sendShareMessage = async () => {
  if (window.ReactNativeWebView) {
    const messageData = {
      action: 'share',
      payload: {
        shareData: await shareWithApp(shareItem.value),
      },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(messageData));
  }
};

/** 공유 취소 에러 판별 */
const isShareCanceledError = (error) => {
  const errorName = error?.name || '';
  const errorMessage = error?.message || '';
  return (
    errorName === 'AbortError' ||
    /abort|cancel|dismiss/i.test(errorName) ||
    /abort|cancel|dismiss/i.test(errorMessage)
  );
};

/** 기기 기본 공유 (navigator.share) */
const shareByNative = async (item) => {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function')
    return false;

  const baseUrl = DEV_MODE ? 'https://d25web.thegam.io' : VITE_WEB_BASE_URL;
  const webUrl = `${baseUrl}/essay/${route.params.essayId}`;

  const shareData = {
    title: item.mainTitle || '낭만에세이',
    text: branchShareDataMap.essay.messagePreset,
    url: webUrl,
  };

  if (typeof navigator.canShare === 'function' && !navigator.canShare(shareData))
    return false;

  await navigator.share(shareData);
  return true;
};

/** 공유하기 버튼 클릭 */
const onPressShare = async (item) => {
  if (!item) return null;

  shareItem.value = item;

  // RN WebView 환경
  if (window.ReactNativeWebView) {
    if (isAndroidWebView) {
      sendShareMessage();
    } else {
      showPostSharePopup.value = true;
    }
    return;
  }

  // 모바일 웹: navigator.share 우선 시도
  const isMobileDevice = window.innerWidth <= 768;
  if (isMobileDevice) {
    try {
      const isShared = await shareByNative(item);
      if (isShared) return;
    } catch (error) {
      if (isShareCanceledError(error)) return;
      DEV_MODE && console.error('기기 기본 공유 실패:', error);
    }
  }

  // 데스크톱 또는 navigator.share 실패 시 카카오 공유
  shareWithKakao(item);
};

const clickShareType = async ({ type }) => {
  if (window.ReactNativeWebView) {
    if (type === 'kakao') shareWithKakao(shareItem.value);
    if (type === 'link') sendShareMessage();
  } else {
    if (type === 'kakao') shareWithKakao(shareItem.value);
    if (type === 'link') {
      const baseUrl = DEV_MODE ? 'https://d25web.thegam.io' : VITE_WEB_BASE_URL;
      const webUrl = `${baseUrl}/essay/${route.params.essayId}`;
      try {
        await navigator.clipboard.writeText(webUrl);
      } catch (_) {}
    }
  }
};

const shareWithApp = async (item) => {
  // 앱에 전달할 페이지
  const page = convertPathToBranchPage(route.path);

  const messagePreset =
    page && branchShareDataMap[page]
      ? branchShareDataMap[page].messagePreset
      : branchShareDataMap['default'].messagePreset;

  // 카톡 공유하기 이미지
  const thumbnailUrl = `${VITE_WEB_BASE_URL}/secure/feedThumbnail.png`;
  const imageUrl = item?.thumbnail?.fileUri || thumbnailUrl;

  const branchLinkPayload = {
    page,
    contentId: item.essayId,
    contentTitle: item.firstTitle,
    imageUrl,
    routePath: route.path,
    login: userId,
  };

  let branchUrl = '';
  branchUrl = await createBranchLink(branchLinkPayload);
  return { branchUrl, branchLinkPayload, messagePreset };
};

/** 카카오 공유하기 */
const shareWithKakao = async (item) => {
  // 앱에 전달할 페이지
  const page = convertPathToBranchPage('/nangman-center/essay');

  // 카톡 공유하기 메시지
  let message =
    page && branchShareDataMap[page]
      ? branchShareDataMap[page].messagePreset
      : branchShareDataMap['default'].messagePreset;

  // 카톡 공유하기 이미지 - 에세이는 text 타입으로 공유하므로 미사용

  let mobileWebUrl = ``;
  // Branch SDK가 초기화되었는지 확인
  if (!isBranchInitialized.value) {
    DEV_MODE && console.error('Branch SDK가 초기화되지 않았습니다.');
  } else {
    try {
      const branchUrl = await createBranchLink({
        page,
        contentId: item.essayId,
        contentTitle: item.firstTitle,
        routePath: route.path,
        login: true,
      });
      if (branchUrl) {
        message += branchUrl;
        mobileWebUrl = branchUrl;
      }
    } catch (e) {
      DEV_MODE && console.log('branch link 생성에 실패하였습니다. :', e);
    }
  }

  const content = modifyEssayContent(message.length, item);
  if (content) message = `${content}`;

  const baseUrl = DEV_MODE ? 'https://d25web.thegam.io' : VITE_WEB_BASE_URL;
  const webUrl = `${baseUrl}/essay/${route.params.essayId}`;
  if (!mobileWebUrl || mobileWebUrl.length < 1) mobileWebUrl = webUrl;

  if (typeof Kakao !== 'undefined') {
    if (!Kakao.isInitialized()) {
      await Kakao.init(import.meta.env.VITE_KAKAO_APP_KEY);
    }
  } else {
    DEV_MODE && console.error('Kakao SDK가 로드되지 않았습니다.');
    return;
  }

  Kakao.Share.sendDefault({
    objectType: 'text',
    text: message,
    link: {
      mobileWebUrl,
      webUrl,
      androidExecutionParams: `screen=${page}&articleId=${item.essayId}`,
      iosExecutionParams: `screen=${page}&articleId=${item.essayId}`,
    },
  });
};


const articleBox = ref(null);

watch(contents, async () => {
  await nextTick();
  const links = articleBox.value?.querySelectorAll('a') || [];
  links.forEach((link) => {
    // target 속성 제거
    link.removeAttribute('target');
    link.setAttribute('rel', 'noopener');
  });
});

/** html속 url 가로채기 */
useSimpleLinkIntercept(articleBox, {
  devMode: DEV_MODE,
  // 필요 시 내부 호스트를 직접 지정:
  // internalHosts: DEV_MODE
  //   ? ['d25web.thegam.io']
  //   : ['nangmancalendar.com', 'www.nangmancalendar.com'],
  debug: false,
});
</script>

<template>
  <div v-if="essayData" class="essay-page">
    <div v-if="isWebView" class="mobile-board__sticky-top">
      <BoardTitleBox :board="board" />
      <div v-if="isWebView" class="essay-view-tit-st2-box essay">
        <div class="like-box">
          <div class="header-like-area">
            <button class="icon-box" @click="patchLike()">
              <IconLike class="icon-like" :class="{ active: likeData.likeClicked }" />
            </button>
            <span class="num">{{ likeData.likeCnt }}</span>
          </div>
          <button class="icon-share none-txt w24" @click="onPressShare(essayData)">공유하기</button>
        </div>
      </div>
    </div>
    <div class="essay-title-box">
      <img
        v-if="essayData.attachFiles && essayData.attachFiles.length"
        :src="essayData.attachFiles[0].publishedUri"
        alt="attached file"
      />
      <div class="txt-box">
        <h1 class="tit">
          {{ essayData.mainTitle }}
        </h1>
        <time class="date" :datetime="essayData.displayDate">
          {{ formatCustomDate(essayData.displayDate) }}
        </time>
      </div>
    </div>

    <article class="hd-inner-base pc between essay-content-box">
      <div ref="articleBox" class="essay-view-content" v-html="contents" />
    </article>

    <section class="btn-com gap-xl center">
      <button class="btn-line w-large bg-w" @click="goViewList">목록으로!</button>
    </section>

    <section class="share-btm-box">
      <div class="hd-inner-base pc between">
        <div class="tit-box" @click="addKakaoChannel">
          <i class="icon-essay" />
          <span v-if="!isWebView"
            >카카오 채널 '낭만만세력' 친구 추가하면 매일매일 에세이를 받을 수 있어요!
          </span>
        </div>
        <div v-if="!isWebView" class="like-box">
          <button class="icon-box" @click="patchLike()">
            <IconLike class="icon-like" :class="{ active: likeData.likeClicked }" />
          </button>
          <span class="num">{{ likeData.likeCnt }}</span>
          <button class="icon-share none-txt w24" @click="onPressShare(essayData)">공유하기</button>
        </div>
      </div>
    </section>
  </div>

  <!-- 모바일 헤더에 좋아요/공유 버튼 (모바일 웹 전용) -->
  <Teleport v-if="essayData && !isWebView" to="#essay-header-actions">
    <div class="essay-header-actions">
      <button class="essay-header-actions__like" @click="patchLike()">
        <IconLike class="icon-like" :class="{ active: likeData.likeClicked }" />
      </button>
      <button
        class="essay-header-actions__share icon-share none-txt w24"
        @click="onPressShare(essayData)"
      >
        공유하기
      </button>
    </div>
  </Teleport>

  <PostSharePopup v-if="showPostSharePopup" @close="closePopSharePopup" @share="clickShareType" />
</template>

<style lang="scss" scoped>
.essay-page {
  background-color: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;

  .essay-content-box {
    display: flex;
    flex: 1;
  }
}
@media (max-width: 768px) {
  .essay-content-box {
    width: 100vw;
  }
  .essay-view-content {
    width: 100vw;
    padding: 14px 0px !important;
  }
  .hd-inner-base {
    width: 100vw;
    &.between {
      display: flex;
      width: 100%;
      padding: 10px 16px;
      justify-content: flex-start;
      align-items: center;
    }
  }

  // 하단 카카오 채널 영역: 모바일에서 아이콘만 표시
  .share-btm-box {
    height: auto;
    padding-bottom: env(safe-area-inset-bottom, 0);

    .hd-inner-base.between {
      min-height: 56px;
    }

    .tit-box span {
      display: none;
    }
    .like-box {
      display: none;
    }
  }
}
.essay-view-content {
  padding: 40px;
  // color: #575757;
  color: #333;
  // font-family: 'Nanum Myeongjo';
  font-family: 'Maruburi', serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 1.8;
}

// 시맨틱 마크업: h1 기본 굵기/색을 div와 동일하게 reset
.essay-title-box .txt-box .tit {
  font-weight: 400;
  color: inherit;
}
</style>

<!-- Teleport된 헤더 버튼 스타일 (scoped 밖) -->
<style lang="scss">
#essay-header-actions {
  display: flex;
  align-items: center;
  height: 40px;
}

.essay-header-actions {
  display: flex;
  align-items: center;
  height: 36px;
  gap: 4px;

  &__like {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    position: relative;
    top: 0px;

    .icon-like {
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        width: 24px;
        height: 24px;
      }

      path {
        fill: #ebebeb;
        stroke: #ebebeb;
      }

      &.active path {
        fill: #eb4c10;
        stroke: #eb4c10;
      }
    }
  }

  &__share {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 36px;
    margin-left: 4px;
    position: relative;
    top: 2px;
  }
}
</style>
